import Component from '@glimmer/component';
import { PhCheckCircle, PhXCircle, PhGraduationCap } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format.ts';
import safeFormatDate from '../../../../../helpers/safe-format-date.ts';
import { Section } from 'tio-ui/components/layout';
import Chip from 'tio-ui/components/chip';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { AiRecommendationExtension as AiRecommendationModel } from 'tio-data/data/resources/ai-recommendation.ext';
import RecommendationCard from './recommendation-card.gts';
import type { DeferReason } from './recommendation-card.gts';

/**
 * Application Analysis View
 * Displays AI-generated analysis for the entire application based on actual API structure
 */
export interface ApplicationAnalysisViewSignature {
  Args: {
    recommendation: AiRecommendationModel;
    application: TasApplicationModel;
  };
}

interface ChargeItem {
  amount: number;
  detail: string;
  date?: string;
  provided_reimbursement_documents: boolean;
  provided_reimbursement_documents_for_associated_course: boolean;
  associated_course_name: string;
  follows_all_policies: boolean;
  is_approved_for_reimbursement: boolean;
  saw_proof_of_payment: boolean;
  proof_of_payment_file_name?: string;
}

interface CreditItem {
  amount: number;
  detail: string;
  proof_of_credit_file_name?: string;
}

interface NameMatch {
  is_name_match: boolean;
  reason: string;
}

interface CourseReimbursementChecklist {
  course_name: string;
  period: string;
  begin_date: string;
  end_date: string;
  document_submission_deadline_date: string;
  course_completion_document_name: string;
  completion_document_submission_date: string;
  completion_document_submitted_within_deadline: boolean;
  course_payment_invoice_document_name: string;
  course_payment_invoice_submission_date: string;
  course_payment_document_submitted_within_deadline: boolean;
  number_of_credits: number;
  grade: string;
  applicant_satisfied_all_requirements_for_reimbursement: boolean;
  reason: string;
}

interface EmployeeEligibility {
  is_eligible: boolean;
  reason: string;
}

interface Proration {
  total_credits_in_application: number;
  total_credits_attempted: number;
  total_credits_passed: number;
}

interface LastNamePresent {
  is_last_name_present: boolean;
  reason: string;
}

interface ReasoningStructure {
  charges?: ChargeItem[];
  credits?: CreditItem[];
  name_match?: NameMatch;
  proration?: Proration;
  last_name_present_in_all_receipts?: LastNamePresent;
  course_reimbursement_checklist?: CourseReimbursementChecklist[];
  employee_eligibility?: EmployeeEligibility;
}

interface RecommendationSummary {
  status: string;
  approved_amount: number;
  available_funds: number;
  requested_amount: number;
  tas_application_id?: string;
  calculation_details?: string;
}

function normalizeWhitespace(text: string) {
  return text.replace(/\s+/g, ' ').trim();
}

interface ResponseJsonStructure {
  reasoning?: ReasoningStructure;
  recommendation_summary?: RecommendationSummary;
  // present on defer_to_human/failure rows; failure rows may carry ONLY
  // { error, defer_reason } with no recommendation_summary at all
  defer_reason?: DeferReason;
  error?: string;
}

class ApplicationAnalysisView extends Component<ApplicationAnalysisViewSignature> {
  get responseData(): ResponseJsonStructure {
    return (this.args.recommendation.responseJson as ResponseJsonStructure) || {};
  }

  get recommendationSummary(): RecommendationSummary | undefined {
    return this.responseData.recommendation_summary;
  }

  get reasoning(): ReasoningStructure {
    return this.responseData.reasoning || {};
  }

  get recommendation(): string {
    return (
      this.recommendationSummary?.status || this.args.recommendation.recommendedTo || 'UNKNOWN'
    );
  }

  get charges(): ChargeItem[] {
    return this.reasoning.charges || [];
  }

  get credits(): CreditItem[] {
    return this.reasoning.credits || [];
  }

  get nameMatch(): NameMatch | undefined {
    return this.reasoning.name_match;
  }

  get courseReimbursementChecklist(): CourseReimbursementChecklist[] {
    return this.reasoning.course_reimbursement_checklist || [];
  }

  get proration(): Proration | undefined {
    return this.reasoning.proration;
  }

  get lastNamePresent(): LastNamePresent | undefined {
    return this.reasoning.last_name_present_in_all_receipts;
  }

  get employeeEligibility(): EmployeeEligibility | undefined {
    return this.reasoning.employee_eligibility;
  }

  get calculationDetails(): string | undefined {
    return this.recommendationSummary?.calculation_details;
  }

  get deferReason() {
    return this.responseData.defer_reason;
  }

  get approvedAmount() {
    return this.recommendationSummary?.approved_amount;
  }

  get requestedAmount() {
    return this.recommendationSummary?.requested_amount;
  }

  get showRecommendationCard() {
    return !!(this.recommendationSummary || this.deferReason);
  }

  get analysisCreatedAt() {
    return this.args.recommendation.createdAt;
  }

  get analysisCount() {
    return this.args.application.aiRecommendations?.length ?? 0;
  }

  /**
   * A provenance string the agent persisted into the recommendation context.
   * Errored contexts describe the failed build rather than the recommendation,
   * so they yield nothing — as do rows written before the agent recorded the
   * field, which render no line at all rather than claiming "unknown".
   */
  #contextVersion(key: string) {
    const context = this.args.recommendation.contextForRecommendation;
    if (!context || context['is_error']) return undefined;

    const version = context[key];
    return typeof version === 'string' && version.length > 0 ? version : undefined;
  }

  /**
   * The prompt that produced this recommendation. Present on rows written since
   * the agent started recording it — including every pre-prompt-store row,
   * which carries the in-code pin.
   */
  get promptVersion() {
    return this.#contextVersion('prompt_version');
  }

  /**
   * Two self-describing formats coexist and both must read sensibly:
   *   `db:<set_key>:v<n>`  — a prompt-store version, where set_key is either
   *                          `global` or `<company_id>/<program_code>`. Matched
   *                          on the trailing `:v<digits>`, because a scoped
   *                          set_key carries its own separator.
   *   `2026.07.30.2`       — the agent's in-code pin (every pre-RFC-094 row),
   *                          a dated build number.
   * Each origin is claimed only from its own shape. A value matching neither —
   * malformed, or a future identity this code predates — is shown exactly as
   * stored: naming an origin we cannot establish is worse for a triage than
   * naming none.
   */
  get promptVersionLabel() {
    const version = this.promptVersion;
    if (!version) return undefined;

    const stored = /^db:(.+):v(\d+)$/.exec(version);
    if (stored) return `Prompt ${stored[1]} v${stored[2]} (prompt store)`;
    if (/^\d{4}\.\d{2}\.\d{2}\.\d+$/.test(version)) return `Prompt ${version} (in code)`;

    return `Prompt ${version}`;
  }

  /**
   * The partner-policy revision this recommendation was judged under (RFC 095).
   * Only rows written since the agent started recording it carry one — nothing
   * can be reconstructed for older rows, so they show no policy line at all.
   */
  get policyVersion() {
    return this.#contextVersion('policy_version');
  }

  /**
   * Stored as `<company_id>-partner_policy-v<n>`, matched on the trailing
   * `-v<digits>`. The company id is dropped: it is already implied by the
   * application on screen, and "Policy v7" is what an approver can actually
   * repeat back. Anything not matching that shape is shown as stored, same
   * rule as the prompt label.
   */
  get policyVersionLabel() {
    const version = this.policyVersion;
    if (!version) return undefined;

    const stamped = /-partner_policy-v(\d+)$/.exec(version);
    return stamped ? `Policy v${stamped[1]}` : `Policy ${version}`;
  }

  /**
   * The note the automated reviewer actually sent to the employee — the
   * decision may have acted on an older analysis than the one displayed
   * (e.g. tio-front-end#9621), so the sent text is read from the
   * application's transition log, not from any recommendation row.
   */
  get sentMessage() {
    const transitionLog = this.args.application.transitionLog || [];
    for (let i = transitionLog.length - 1; i >= 0; i--) {
      const transition = transitionLog[i];
      const reason = transition?.reason;
      const note = reason?.note;
      if (!transition || !reason || !note) continue;
      // logs predating the automated flag carry no marker either way — a
      // noted transition without a human actor (`by`) is system-authored
      const isAutomated = reason.automated ?? !reason.by;
      if (isAutomated) {
        return { note, timestamp: transition.timestamp };
      }
    }
    return undefined;
  }

  get showSentMessage() {
    const sent = this.sentMessage;
    if (!sent) return false;
    // when identical, the card's calculation details already ARE the sent text;
    // whitespace-insensitive so multi-line formatting differences don't count
    return normalizeWhitespace(sent.note) !== normalizeWhitespace(this.calculationDetails ?? '');
  }

  get sentMessageHint() {
    const sentAt = new Date(this.sentMessage?.timestamp ?? '').getTime();
    const analyzedAt = new Date(this.analysisCreatedAt ?? '').getTime();
    if (Number.isFinite(sentAt) && Number.isFinite(analyzedAt) && analyzedAt > sentAt) {
      return 'The analysis above was generated after this message was sent — documents or context may have changed.';
    }
    return 'This message was sent based on a different analysis than the one shown above.';
  }

  <template>
    <div class="p-6 space-y-6">
      {{! Main Recommendation Card }}
      <div>
        <h3 class="tio-text-base-semibold mb-4 tio-fg-primary">AI Recommendation</h3>
        {{#if this.showRecommendationCard}}
          <RecommendationCard
            @recommendation={{this.recommendation}}
            @calculationDetails={{this.calculationDetails}}
            @approvedAmount={{this.approvedAmount}}
            @requestedAmount={{this.requestedAmount}}
            @deferReason={{this.deferReason}}
          />
          {{#if this.analysisCreatedAt}}
            <p class="tio-text-caption mt-2" data-test-ai-analysis-date>
              Analysis generated
              {{safeFormatDate
                this.analysisCreatedAt
                month="short"
                day="2-digit"
                year="numeric"
                hour="numeric"
                minute="2-digit"
                hour12=true
                timeZoneName="short"
              }}
              {{#if (gt this.analysisCount 1)}}({{this.analysisCount}}
                analyses for this application){{/if}}
            </p>
          {{/if}}
          {{#if this.promptVersionLabel}}
            <p
              class="tio-text-caption mt-1"
              title={{this.promptVersion}}
              data-test-ai-prompt-version
            >
              {{this.promptVersionLabel}}
            </p>
          {{/if}}
          {{#if this.policyVersionLabel}}
            <p
              class="tio-text-caption mt-1"
              title={{this.policyVersion}}
              data-test-ai-policy-version
            >
              {{this.policyVersionLabel}}
            </p>
          {{/if}}
        {{/if}}
      </div>

      {{! What the automated reviewer actually sent — may come from an older
          analysis than the one displayed above }}
      {{#if this.showSentMessage}}
        {{#let this.sentMessage as |sentMessage|}}
          {{#if sentMessage}}
            <Section>
              <:header>Message Sent to Employee</:header>
              <:body>
                <div data-test-ai-sent-message>
                  <p class="tio-text-caption mb-1" data-test-ai-sent-message-date>Sent
                    {{safeFormatDate
                      sentMessage.timestamp
                      month="short"
                      day="2-digit"
                      year="numeric"
                      hour="numeric"
                      minute="2-digit"
                      hour12=true
                      timeZoneName="short"
                    }}</p>
                  <p class="text-sm tio-fg-secondary leading-relaxed">{{sentMessage.note}}</p>
                  <p class="tio-text-caption mt-2" data-test-ai-sent-message-hint>
                    {{this.sentMessageHint}}
                  </p>
                </div>
              </:body>
            </Section>
          {{/if}}
        {{/let}}
      {{/if}}

      {{! Employee Eligibility }}
      {{#if this.employeeEligibility}}
        <Section>
          <:header>Employee Eligibility</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.employeeEligibility.is_eligible}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Eligible</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="danger" @size="sm">Not Eligible</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Course Reimbursement Checklist }}
      {{#if this.courseReimbursementChecklist.length}}
        <Section>
          <:header>Course Reimbursement Checklist</:header>
          <:body>
            <div class="space-y-4">
              {{#each this.courseReimbursementChecklist as |course|}}
                <div class="tio-row-top p-3 tio-surface-sunken">
                  {{#if course.applicant_satisfied_all_requirements_for_reimbursement}}
                    <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="tio-row-tight mb-1">
                      <PhGraduationCap @size="16" class="tio-fg-muted" />
                      <p class="tio-text-sm-semibold tio-fg-primary">{{course.course_name}}</p>
                    </div>
                    {{#if course.grade}}
                      <p class="tio-text-caption mb-1">Grade:
                        {{course.grade}}</p>
                    {{/if}}
                    {{#if course.period}}
                      <p class="tio-text-caption mb-1">Period:
                        {{course.period}}</p>
                    {{/if}}
                    <p class="tio-text-caption mb-1">Credits:
                      {{course.number_of_credits}}</p>
                    <p class="text-sm tio-fg-secondary mt-2">{{course.reason}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Name Match Verification }}
      {{#if this.nameMatch}}
        <Section>
          <:header>Name Verification</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.nameMatch.is_name_match}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Name Verified</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Name Mismatch</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Last Name Present in Receipts }}
      {{#if this.lastNamePresent}}
        <Section>
          <:header>Last Name in Receipts</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.lastNamePresent.is_last_name_present}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Verified</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Not Found</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Proration }}
      {{#if this.proration}}
        <Section>
          <:header>Credit Proration</:header>
          <:body>
            <div class="space-y-2">
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits in Application</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_in_application}}</span>
              </div>
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits Attempted</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_attempted}}</span>
              </div>
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits Passed</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_passed}}</span>
              </div>
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Charges Breakdown }}
      {{#if this.charges.length}}
        <Section>
          <:header>Charges Analysis</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.charges as |charge|}}
                <div class="tio-row-top p-3 tio-surface-sunken">
                  {{#if charge.is_approved_for_reimbursement}}
                    <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="tio-row-between mb-1">
                      <p class="tio-text-sm-semibold tio-fg-primary">{{formatAsDollars
                          charge.amount
                          (hash cents=false)
                        }}</p>
                      {{#if charge.is_approved_for_reimbursement}}
                        <Chip @intent="success" @size="sm">Approved</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">Not Approved</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm tio-fg-secondary mb-2">{{charge.detail}}</p>
                    {{#if charge.saw_proof_of_payment}}
                      <div class="tio-row-extra-tight tio-text-caption">
                        <PhCheckCircle @size="14" class="tio-fg-success" />
                        <span>Proof of payment verified</span>
                        {{#if charge.proof_of_payment_file_name}}
                          <span class="tio-fg-muted">•
                            {{charge.proof_of_payment_file_name}}</span>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Credits/Discounts }}
      {{#if this.credits.length}}
        <Section>
          <:header>Credits & Discounts</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.credits as |credit|}}
                <div class="tio-row-top p-3 app-status-panel app-status-panel--info rounded-lg">
                  <div class="flex-1">
                    <div class="tio-row-between mb-1">
                      <p class="tio-text-sm-semibold tio-fg-primary">-{{formatAsDollars
                          credit.amount
                          (hash cents=false)
                        }}</p>
                      {{#if credit.proof_of_credit_file_name}}
                        <Chip
                          @intent="primary"
                          @size="sm"
                        >{{credit.proof_of_credit_file_name}}</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm tio-fg-secondary">{{credit.detail}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}
    </div>
  </template>
}

export default ApplicationAnalysisView;
